import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePermissions } from "@/hooks/use-permissions";
import { cn } from "@/lib/utils";
import { PermissaoEnum } from "@workflow/types";

export interface PageTabItem {
  /** Identificador único da aba. */
  value: string;
  label: string;
  icon?: React.ElementType;
  content: ReactNode;

  /** Filtra a aba via `usePermissions().hasAnyPermission`. Sem `permissions`, sempre visível. */
  permissions?: PermissaoEnum[];

  /**
   * Oculta a aba quando `false` (ex.: depende de dados que não existem). Default
   * `true`. Evita spread condicional na página — passe o array completo e deixe
   * cada aba declarar sua própria visibilidade.
   */
  visible?: boolean;

  /** Aba aparece, mas inativa (com tooltip opcional). */
  disabled?: boolean;
  disabledTooltip?: string;

  /** Conteúdo extra ao lado do label (ex.: contador). */
  badge?: ReactNode;

  /** Disparado ao ativar a aba — útil para carregar dados sob demanda. */
  onActivate?: () => void;
}

interface PageTabsProps {
  tabs: PageTabItem[];
  /** Aba ativa (modo controlado). Se omitido, o componente controla o próprio estado. */
  value?: string;
  /**
   * Sincroniza a aba ativa com este search param da URL (ex.: `?tab=custos`),
   * tornando o link compartilhável. Ignorado em modo controlado (`value`).
   */
  paramKey?: string;
  /** Aba inicial no modo não-controlado. Default: primeira aba visível. */
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Ação geral da página, à direita da linha de abas (no celular, abaixo do seletor). */
  trailing?: ReactNode;
  className?: string;
  listClassName?: string;
  contentClassName?: string;
}

// Estilo "boxed em track": os cards vivem dentro de um trilho cinza (TabsList);
// a aba ativa fica branca e elevada, com um accent (risco) na cor primária
// embaixo — marca a seleção sem fugir do estilo boxed.
const triggerClass = cn(
  "relative h-auto gap-2 rounded-md border border-transparent px-4 pb-2.5 pt-2 text-sm font-medium",
  "transition-all duration-200 ease-out",
  // Inativa: texto apagado + feedback sutil de hover no trilho.
  "text-muted-foreground hover:bg-background/60 hover:text-foreground",
  // Ativa: card branco elevado, borda fina e ícone em destaque.
  "data-[state=active]:bg-background data-[state=active]:text-foreground",
  "data-[state=active]:border-border/60 data-[state=active]:shadow-sm",
  "data-[state=active]:hover:bg-background",
  "data-[state=active]:[&>svg]:text-primary",
  // Accent inferior na aba ativa: risco curto na cor primária.
  "data-[state=active]:after:absolute data-[state=active]:after:bottom-1 data-[state=active]:after:left-4 data-[state=active]:after:right-4 data-[state=active]:after:h-0.5 data-[state=active]:after:rounded-full data-[state=active]:after:bg-primary data-[state=active]:after:content-['']",
  // Ícone acompanha a transição de cor do texto.
  "[&>svg]:shrink-0 [&>svg]:transition-colors",
  "disabled:pointer-events-none disabled:opacity-50",
);

/**
 * O badge precisa ser um elemento próprio: uma string colada ao label vira **um único**
 * item de flex anônimo, e o `gap-2` do trigger não separa nada ("Reajustes1").
 */
function TabBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground/10 px-1.5 text-[11px] font-semibold tabular-nums leading-none">
      {children}
    </span>
  );
}

/**
 * Abas genéricas (config-driven, estilo underline) para páginas de detalhe.
 * Suporta filtro por permissão, lazy-load por aba (`onActivate`), aba
 * desabilitada com tooltip, badge no label e fallback para `<Select>` no mobile.
 */
export function PageTabs({
  tabs,
  value,
  paramKey,
  defaultValue,
  onValueChange,
  trailing,
  className,
  listClassName,
  contentClassName,
}: PageTabsProps) {
  const isMobile = useIsMobile();
  const { hasAnyPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleTabs = useMemo(
    () =>
      tabs.filter(
        (tab) => (tab.visible ?? true) && (!tab.permissions || hasAnyPermission(tab.permissions)),
      ),
    [tabs, hasAnyPermission],
  );

  const firstVisibleValue = visibleTabs[0]?.value ?? "";
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? firstVisibleValue);

  // URL vira fonte da verdade quando `paramKey` está setado (e não controlado).
  const urlValue = paramKey ? searchParams.get(paramKey) : null;
  const validUrlValue = visibleTabs.some((tab) => tab.value === urlValue) ? urlValue : null;
  const activeValue = isControlled ? value : (validUrlValue ?? internalValue);

  // Garante uma aba válida quando a lista visível muda (ex.: permissões resolvem tarde).
  useEffect(() => {
    if (isControlled) return;
    if (!visibleTabs.some((tab) => tab.value === internalValue)) {
      setInternalValue(firstVisibleValue);
    }
  }, [isControlled, visibleTabs, internalValue, firstVisibleValue]);

  // Dispara onActivate da aba inicial no mount — handleChange só cobre trocas.
  useEffect(() => {
    tabs.find((tab) => tab.value === activeValue)?.onActivate?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só no mount
  }, []);

  const handleChange = useCallback(
    (next: string) => {
      if (!isControlled) {
        setInternalValue(next);
        if (paramKey) {
          setSearchParams(
            (prev) => {
              prev.set(paramKey, next);
              return prev;
            },
            { replace: true },
          );
        }
      }
      onValueChange?.(next);
      tabs.find((tab) => tab.value === next)?.onActivate?.();
    },
    [isControlled, paramKey, setSearchParams, onValueChange, tabs],
  );

  if (!visibleTabs.length) return null;

  return (
    <Tabs
      value={activeValue}
      onValueChange={handleChange}
      className={cn("w-full gap-0", className)}
    >
      <div
        className={cn(trailing && "flex flex-wrap items-center justify-between gap-x-3 gap-y-2")}
      >
        {isMobile ? (
          <Select value={activeValue} onValueChange={handleChange}>
            <SelectTrigger className="mb-3 h-11 w-full rounded-lg border-border/70 shadow-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {visibleTabs.map((tab) => (
                <SelectItem
                  key={tab.value}
                  value={tab.value}
                  disabled={tab.disabled}
                  className="cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    {tab.icon && <tab.icon className="h-4 w-4 text-muted-foreground" />}
                    {tab.label}
                    {tab.badge != null && <TabBadge>{tab.badge}</TabBadge>}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <TabsList
            className={cn(
              "inline-flex h-auto w-fit items-center gap-1 rounded-lg border border-border/70 bg-muted/60 p-1",
              "shadow-inner",
              listClassName,
            )}
          >
            {visibleTabs?.map((tab) => {
              const trigger = (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  disabled={tab.disabled}
                  className={triggerClass}
                >
                  {tab.icon && <tab.icon className="h-4 w-4" />}
                  {tab.label}
                  {tab.badge != null && <TabBadge>{tab.badge}</TabBadge>}
                </TabsTrigger>
              );

              // Trigger desabilitado tem pointer-events-none; o tooltip precisa de um wrapper.
              if (tab.disabled && tab.disabledTooltip) {
                return (
                  <TooltipProvider key={tab.value}>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="inline-flex cursor-not-allowed">{trigger}</span>
                      </TooltipTrigger>
                      <TooltipContent>{tab.disabledTooltip}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }

              return trigger;
            })}
          </TabsList>
        )}
        {trailing}
      </div>

      {visibleTabs
        .filter((tab) => !tab.disabled)
        .map((tab) => (
          <TabsContent
            key={tab.value}
            value={tab.value}
            className={cn(
              "mt-4 duration-300 animate-in fade-in-50 slide-in-from-bottom-1",
              contentClassName,
            )}
          >
            {tab.content}
          </TabsContent>
        ))}
    </Tabs>
  );
}
